import type PptxGenJS from "pptxgenjs";
import {
  applyTextCase,
  buildGraphicRenderPlan,
  buildPptxChartPayload,
  buildPptxTableRows,
  getLayerChart,
  getLayerGraphic,
  getLayerTable,
  hexToPptxColor,
  layerRenderStyle,
  normRectToPptxIn,
  pptxChartColors,
  pptxFontSizePt,
  pptxSafeFontFace,
  resolveTextStyle,
  resolveFittedTextStyle,
  snapSurfFontWeight,
  isTextRasterLayer,
  getTextRasterAssetKey,
  getRasterBackdrop,
  chromaKeyHexForLayer,
  getTextBoxBackground,
  isComposedBackgroundMode,
  needsRasterChromaKeyPrep,
  usesRasterSolidPanel,
  sortedLayers,
  stripSpeakerScriptPlain,
  surfPreviewScrimStyle,
  SURF_PPTX_SLIDE_HEIGHT_IN,
  SURF_PPTX_SLIDE_WIDTH_IN,
  type SurfLayer,
  type SurfSlide,
  type SurfStyle,
} from "@vida/surf-core";
import {
  collectLayerCrops,
  collectLayerCovers,
  surfExportImageFit,
  type SurfPdfExportInput,
} from "./surfPdfExport.js";
import {
  prefetchImages,
  layerPayloadsFromUrls,
  collectExportImageUrls,
  applyLayerCropsToPayloads,
  applyLayerCoversToPayloads,
  type ImagePayload,
} from "./imagePayload.js";
import { loadPptxGenConstructor } from "./pptxgenLoader.js";

export type SurfPptxExportInput = SurfPdfExportInput;

/** PptxGenJS shape names — static enum lives on instances, not the constructor. */
const PPTX_SHAPE_RECT = "rect" as const;
const PPTX_SHAPE_ROUND_RECT = "roundRect" as const;

function pptxAlign(align: "left" | "center" | "right"): "left" | "center" | "right" {
  return align;
}

function addSolidBackground(pptxSlide: PptxGenJS.Slide, slide: SurfSlide, style: SurfStyle): void {
  const manual = slide.background.solidColor?.trim();
  const fillHex =
    manual && /^#[0-9A-Fa-f]{6}$/i.test(manual) ? manual : style.palette.surface;
  const color = hexToPptxColor(fillHex);
  pptxSlide.addShape(PPTX_SHAPE_RECT, {
    x: 0,
    y: 0,
    w: SURF_PPTX_SLIDE_WIDTH_IN,
    h: SURF_PPTX_SLIDE_HEIGHT_IN,
    fill: { color },
    line: { color, width: 0 },
  });
}

function addScrimOverlay(slide: PptxGenJS.Slide, style: SurfStyle): void {
  const scrimCss = surfPreviewScrimStyle(style);
  if (!scrimCss) return;
  const leg = style.legibility;
  if (leg.strategy !== "scrim" && leg.strategy !== "box") return;
  const scrim = leg.scrim ?? { color: "#000000", opacity: 0.35 };
  const opacity = typeof scrimCss.opacity === "number" ? scrimCss.opacity : scrim.opacity;
  slide.addShape(PPTX_SHAPE_RECT, {
    x: 0,
    y: 0,
    w: SURF_PPTX_SLIDE_WIDTH_IN,
    h: SURF_PPTX_SLIDE_HEIGHT_IN,
    fill: {
      color: hexToPptxColor(scrim.color),
      transparency: Math.round((1 - opacity) * 100),
    },
    line: { width: 0 },
  });
}

function addBackgroundImage(slide: PptxGenJS.Slide, payload: ImagePayload): void {
  slide.addImage({
    data: payload.dataUrl,
    x: 0,
    y: 0,
    w: SURF_PPTX_SLIDE_WIDTH_IN,
    h: SURF_PPTX_SLIDE_HEIGHT_IN,
  });
}

function addTextLayer(slide: PptxGenJS.Slide, layer: SurfLayer, theme: SurfStyle): void {
  const rect = normRectToPptxIn(layer);
  const ts = resolveFittedTextStyle(layer, theme);
  const render = layerRenderStyle(layer, theme, SURF_PPTX_SLIDE_HEIGHT_IN * 96);
  const text = applyTextCase(layer.content?.trim() ?? "", ts.case);
  if (!text) return;

  const boxBg = getTextBoxBackground(layer);
  if (boxBg) {
    slide.addShape("rect", {
      x: rect.x,
      y: rect.y,
      w: rect.w,
      h: rect.h,
      fill: { color: hexToPptxColor(boxBg.color), transparency: Math.round((1 - boxBg.opacity) * 100) },
      line: { type: "none" },
    });
  }

  // PptxGenJS does not embed custom fonts — PowerPoint substitutes if missing.
  // See public/fonts/LICENSES.md; use PDF export for pixel-perfect typography.
  const snappedWeight = snapSurfFontWeight(ts.font, ts.weight);
  slide.addText(text, {
    x: rect.x,
    y: rect.y,
    w: rect.w,
    h: rect.h,
    fontSize: pptxFontSizePt(ts.sizeRel),
    fontFace: pptxSafeFontFace(ts.font),
    bold: snappedWeight >= 700,
    color: hexToPptxColor(render.fillText),
    align: pptxAlign(ts.align),
    valign: "top",
    rotate: layer.rotation,
    margin: 0,
    wrap: true,
  });
}

function addShapeLayer(slide: PptxGenJS.Slide, layer: SurfLayer, theme: SurfStyle): void {
  const rect = normRectToPptxIn(layer);
  slide.addShape(PPTX_SHAPE_ROUND_RECT, {
    x: rect.x,
    y: rect.y,
    w: rect.w,
    h: rect.h,
    fill: { color: hexToPptxColor(theme.palette.accent) },
    line: { width: 0 },
    rotate: layer.rotation,
  });
}

function addRasterBackdropPanel(
  slide: PptxGenJS.Slide,
  layer: SurfLayer,
  theme: SurfStyle,
): void {
  if (!usesRasterSolidPanel(layer)) return;
  const rect = normRectToPptxIn(layer);
  const hex = getRasterBackdrop(layer, theme);
  slide.addShape(PPTX_SHAPE_RECT, {
    x: rect.x,
    y: rect.y,
    w: rect.w,
    h: rect.h,
    fill: { color: hexToPptxColor(hex) },
    line: { width: 0 },
    rotate: layer.rotation,
  });
}

function addImageLayer(
  slide: PptxGenJS.Slide,
  layer: SurfLayer,
  payload: ImagePayload | null,
): void {
  if (!payload) {
    addImagePlaceholder(slide, layer);
    return;
  }
  const rect = normRectToPptxIn(layer);
  // Fit allineato al canvas (via resolver condiviso con l'export PDF):
  //  - cover   → payload bakato al box aspect (center-crop) → full-rect, niente sizing.
  //  - contain → sizing contain (letterbox, come raster text)
  //  - fill    → nessun sizing: la regione crop bakata riempie il box.
  const fit = surfExportImageFit(layer);
  const sizing =
    fit === "contain" ? ({ type: "contain", w: rect.w, h: rect.h } as const) : undefined;
  slide.addImage({
    data: payload.dataUrl,
    x: rect.x,
    y: rect.y,
    w: rect.w,
    h: rect.h,
    rotate: layer.rotation,
    ...(sizing ? { sizing } : {}),
  });
}

function addImagePlaceholder(slide: PptxGenJS.Slide, layer: SurfLayer): void {
  const rect = normRectToPptxIn(layer);
  slide.addShape(PPTX_SHAPE_RECT, {
    x: rect.x,
    y: rect.y,
    w: rect.w,
    h: rect.h,
    fill: { color: "1E293B", transparency: 40 },
    line: { color: "64748B", width: 1 },
  });
  slide.addText(layer.content || "Image", {
    x: rect.x,
    y: rect.y + rect.h / 2 - 0.15,
    w: rect.w,
    h: 0.3,
    fontSize: 12,
    align: "center",
    color: "94A3B8",
  });
}

function addTableLayer(slide: PptxGenJS.Slide, layer: SurfLayer, theme: SurfStyle): void {
  const rect = normRectToPptxIn(layer);
  const rows = buildPptxTableRows(getLayerTable(layer), theme);
  const bodyTs = resolveTextStyle({ ...layer, type: "text", textRole: "body" }, theme);
  slide.addTable(rows, {
    x: rect.x,
    y: rect.y,
    w: rect.w,
    h: rect.h,
    fontSize: Math.max(8, pptxFontSizePt(bodyTs.sizeRel) * 0.55),
    fontFace: pptxSafeFontFace(bodyTs.font),
    border: { type: "solid", color: hexToPptxColor(theme.palette.muted), pt: 0.75 },
    margin: 0.04,
    valign: "middle",
  });
}

function addChartLayer(slide: PptxGenJS.Slide, layer: SurfLayer, theme: SurfStyle): void {
  const rect = normRectToPptxIn(layer);
  const payload = buildPptxChartPayload(getLayerChart(layer));
  const seriesData =
    payload.type === "pie" && payload.series[0] ? [payload.series[0]] : payload.series;
  slide.addChart(
    payload.type,
    seriesData.map((s) => ({ name: s.name, labels: s.labels, values: s.values })),
    {
      x: rect.x,
      y: rect.y,
      w: rect.w,
      h: rect.h,
      chartColors: pptxChartColors(theme, seriesData.length),
      showLegend: payload.type !== "pie" && payload.series.length > 1,
    },
  );
}

function addGraphicLayer(slide: PptxGenJS.Slide, layer: SurfLayer, theme: SurfStyle): void {
  const rect = normRectToPptxIn(layer);
  const plan = buildGraphicRenderPlan(getLayerGraphic(layer), theme);
  const mapX = (n: number) => rect.x + n * rect.w;
  const mapY = (n: number) => rect.y + n * rect.h;
  const mapW = (n: number) => n * rect.w;
  const mapH = (n: number) => n * rect.h;

  for (const box of plan.rects) {
    slide.addShape(PPTX_SHAPE_ROUND_RECT, {
      x: mapX(box.x),
      y: mapY(box.y),
      w: mapW(box.w),
      h: mapH(box.h),
      fill: { color: hexToPptxColor(box.fill.length === 9 ? box.fill.slice(0, 7) : box.fill) },
      line: { width: 0 },
      rotate: layer.rotation,
    });
  }

  for (const t of plan.texts) {
    const bodyTs = resolveTextStyle({ ...layer, type: "text", textRole: "body" }, theme);
    const lines = [t.text, t.subtext].filter(Boolean).join("\n");
    if (!lines) continue;
    slide.addText(lines, {
      x: mapX(t.x),
      y: mapY(t.y),
      w: mapW(t.w),
      h: mapH((t.size ?? 0.05) + (t.subtext ? (t.subSize ?? 0.035) : 0)),
      fontSize: Math.max(8, (t.size ?? 0.05) * rect.h * 72 * 0.55),
      fontFace: pptxSafeFontFace(bodyTs.font),
      color: hexToPptxColor(t.fill),
      align: t.anchor === "middle" ? "center" : "left",
      valign: "top",
      margin: 0.02,
      rotate: layer.rotation,
    });
  }
}

function buildSlide(
  pptxSlide: PptxGenJS.Slide,
  slide: SurfSlide,
  theme: SurfStyle,
  bg: ImagePayload | null,
  layerPayloads: Map<string, ImagePayload>,
): void {
  if (bg) addBackgroundImage(pptxSlide, bg);
  else addSolidBackground(pptxSlide, slide, theme);

  if (!isComposedBackgroundMode(theme)) addScrimOverlay(pptxSlide, theme);

  for (const layer of sortedLayers(slide)) {
    addRasterBackdropPanel(pptxSlide, layer, theme);
    if (layer.type === "text") {
      if (isTextRasterLayer(layer) && getTextRasterAssetKey(layer)) {
        addImageLayer(pptxSlide, layer, layerPayloads.get(layer.id) ?? null);
      } else {
        addTextLayer(pptxSlide, layer, theme);
      }
    } else if (layer.type === "shape") addShapeLayer(pptxSlide, layer, theme);
    else if (layer.type === "image") addImageLayer(pptxSlide, layer, layerPayloads.get(layer.id) ?? null);
    else if (layer.type === "table") addTableLayer(pptxSlide, layer, theme);
    else if (layer.type === "chart") addChartLayer(pptxSlide, layer, theme);
    else if (layer.type === "graphic") addGraphicLayer(pptxSlide, layer, theme);
  }
}

/** Client-side PPTX — testo in text box nativi (PptxGenJS). */
export async function exportSurfDeckToPptx(
  input: SurfPptxExportInput,
  filename?: string,
): Promise<Blob> {
  const { deck, slideBackgroundUrls = {}, layerImageUrls = {} } = input;
  if (!deck.slides.length) throw new Error("Deck senza slide.");

  const pptxgen = await loadPptxGenConstructor();
  const pptx = new pptxgen();

  pptx.defineLayout({
    name: "SURF_16_9",
    width: SURF_PPTX_SLIDE_WIDTH_IN,
    height: SURF_PPTX_SLIDE_HEIGHT_IN,
  });
  pptx.layout = "SURF_16_9";
  pptx.author = "Surf";
  pptx.title = deck.title;

  const urlBySlide = new Map<string, string>();
  for (const s of deck.slides) {
    const fromMap = slideBackgroundUrls[s.id];
    if (fromMap) urlBySlide.set(s.id, fromMap);
  }

  const allUrls = collectExportImageUrls(slideBackgroundUrls, layerImageUrls);
  const chromaKeyByLayerId: Record<string, string> = {};
  for (const slide of deck.slides) {
    for (const layer of slide.layers) {
      if (needsRasterChromaKeyPrep(layer)) {
        chromaKeyByLayerId[layer.id] = chromaKeyHexForLayer(layer, deck.theme);
      }
    }
  }
  const imageCache = await prefetchImages(allUrls, {
    chromaKeyByLayerId,
    layerUrls: layerImageUrls,
  });
  const layerPayloads = layerPayloadsFromUrls(layerImageUrls, imageCache);
  await applyLayerCropsToPayloads(collectLayerCrops(deck), layerPayloads);
  await applyLayerCoversToPayloads(collectLayerCovers(deck), layerPayloads);

  for (const slide of deck.slides) {
    const pptxSlide = pptx.addSlide();
    const bgUrl = urlBySlide.get(slide.id);
    const bg = bgUrl ? imageCache.get(bgUrl) ?? null : null;
    buildSlide(pptxSlide, slide, deck.theme, bg, layerPayloads);
    const scriptNotes = stripSpeakerScriptPlain(slide.speakerScript?.text ?? "");
    const whaleNotes = (slide.speakerScript?.notes ?? [])
      .map((n) => {
        const q = n.question?.trim() || n.source?.question?.trim();
        const qLine = q ? `Q: ${stripSpeakerScriptPlain(q)}\n` : "";
        return `${qLine}${n.title}\n\n${stripSpeakerScriptPlain(n.body)}`;
      })
      .join("\n\n---\n\n");
    const notes = [scriptNotes, whaleNotes].filter(Boolean).join("\n\n--- Approfondimenti ---\n\n");
    if (notes) pptxSlide.addNotes(notes);
  }

  const safeName = (filename ?? `${deck.title || "presentazione"}.pptx`).replace(/[^\w.\- ]+/g, "_");
  const finalName = safeName.endsWith(".pptx") ? safeName : `${safeName}.pptx`;

  const blob = (await pptx.write({ outputType: "blob" })) as Blob;
  triggerDownload(blob, finalName);
  return blob;
}

function triggerDownload(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
